import type { TourSelection } from './tourSteps';

/**
 * Pure resolution of a step's named selection against the live roster — kept
 * free of DOM and React so the picking rules stay unit-testable.
 */

export interface TourRoster {
  /** Stable roster order: left team first, then right. */
  order: string[];
  /** Player id → team id. */
  teamOf: Record<string, string>;
  /** The player the report is written for. */
  focusId: string;
}

/**
 * The players a selection step ends on, in roster order (so the comparison
 * cards read left-to-right the same way the header does).
 *
 * Degrades on thin rosters: a missing opponent or teammate is simply left out
 * rather than faked, so a step never selects a player twice or a phantom id.
 */
export function selectionTarget(
  kind: TourSelection,
  roster: TourRoster,
): string[] {
  const { order, teamOf, focusId } = roster;
  if (!order.includes(focusId)) return order.slice(0, 1);
  if (kind === 'me') return [focusId];
  if (kind === 'all') return [...order];

  const myTeam = teamOf[focusId];
  const opponent = order.find((id) => id !== focusId && teamOf[id] !== myTeam);
  const teammate = order.find((id) => id !== focusId && teamOf[id] === myTeam);

  const picked = new Set([focusId]);
  if (opponent) picked.add(opponent);
  if (kind === 'trio' && teammate) picked.add(teammate);
  return order.filter((id) => picked.has(id));
}

/**
 * How to get from `current` to `target`: drop whatever isn't wanted in one
 * silent step, then tap the rest in one at a time.
 *
 * `keep` is applied instantly (a correction, not a demonstration — nobody
 * needs to watch a player being un-picked), while `adds` are the taps the
 * tour actually performs. `snapshots` are the successive selections to apply,
 * one per tap, so each tap lands a real state change.
 */
export interface SelectionMoves {
  keep: string[];
  adds: string[];
  snapshots: string[][];
}

export function selectionMoves(
  current: string[],
  target: string[],
): SelectionMoves {
  const keep = current.filter((id) => target.includes(id));
  // The roster can never be empty (the report always shows someone), so a
  // selection with nothing worth keeping falls back to the target's lead.
  const start = keep.length ? keep : target.slice(0, 1);
  const adds = target.filter((id) => !start.includes(id));

  const snapshots: string[][] = [];
  const live = new Set(start);
  for (const id of adds) {
    live.add(id);
    snapshots.push(target.filter((t) => live.has(t)));
  }
  return { keep: start, adds, snapshots };
}
